/**
 * cave_light_bake — the cave light field's INPUTS for one real cave (shader gaps program, agent A, 2026-09-29).
 * Worker-safe (no three.js, no DOM): the cave worker calls `bakeCaveField` right after it meshes the cavity, the
 * harness calls it in node on a real site, and the module takes `crystalPlacements` from here so the crystals it
 * draws are exactly the emitters the bake lit the rock with.
 *
 * The occluders are what the renderer draws around a cave: the cavity mesh (rock, its winding facing the air) plus
 * the ground around the mouth as 2 m triangles with the chunk assembler's own cut (`caveCutsTriangle` — the hole the
 * tunnel enters by). Rays that leave that domain march a coarse height grid of the hills around (a hillside above the
 * mouth is ground, not sky). The signed field that classifies probes is the mesher's own: air = the cavity
 * (`caveDensity` < 0) or above the ground-and-outcrop (`caveFieldAt`).
 */
import { terrainHeight } from '@shared/terrain.js';
import type { CaveSite } from '@shared/caves.js';
import { caveDensity, caveCutsTriangle, portalDome, type CavePlan, type CaveMesh } from './cave_gen.js';
import { bakeCaveLight, groundPatch, mergeOccluders, type CaveLightEmitter, type CaveLightField } from './cave_light.js';

/** The crystals' emissive (three sRGB hex) and intensity — the module's crystal material uses the same two numbers. */
export const CRYSTAL_EMISSIVE_HEX = 0x4fb8ff;
export const CRYSTAL_EMISSIVE_INTENSITY = 2.2;
/** A crystal is an icosahedron of this circumradius scaled (s, 1.8 s, s). */
export const CRYSTAL_RADIUS = 0.35;
export const CRYSTAL_EVERY = 2;
/** Linear-light albedo of the damp cave rock (the triplanar highland texture × the material's ~0.64 darkening). */
export const CAVE_ROCK_ALBEDO = 0.2;
/** The hillside around the mouth. */
export const CAVE_GROUND_ALBEDO = 0.22;
/** Probe spacing (m): ~3 probes across a 4.4 m tunnel. */
export const CAVE_PROBE_SPACING = 1.5;
export const CAVE_PROBE_RAYS = 64;

function srgbToLinear(c: number): number { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
/** The crystals' radiance in scene units (linear emissive × intensity). */
export function crystalRadiance(): [number, number, number] {
  const r = ((CRYSTAL_EMISSIVE_HEX >> 16) & 255) / 255, g = ((CRYSTAL_EMISSIVE_HEX >> 8) & 255) / 255, b = (CRYSTAL_EMISSIVE_HEX & 255) / 255;
  return [srgbToLinear(r) * CRYSTAL_EMISSIVE_INTENSITY, srgbToLinear(g) * CRYSTAL_EMISSIVE_INTENSITY, srgbToLinear(b) * CRYSTAL_EMISSIVE_INTENSITY];
}

export interface CrystalPlacement { x: number; y: number; z: number; s: number; rx: number; ry: number; rz: number }

/** Crystals along the walls (every CRYSTAL_EVERY path point, offset to alternating walls) — mouth-relative. */
export function crystalPlacements(path: ReadonlyArray<{ x: number; y: number; z: number; r: number }>): CrystalPlacement[] {
  const n = Math.max(1, Math.floor(path.length / CRYSTAL_EVERY));
  const out: CrystalPlacement[] = [];
  for (let i = 1; i < path.length && out.length < n; i += CRYSTAL_EVERY) {
    const p = path[i], q = path[i - 1];
    const dx = p.x - q.x, dz = p.z - q.z, l = Math.hypot(dx, dz) || 1;
    const side = i % 4 < 2 ? 1 : -1;
    const wx = p.x + (-dz / l) * (p.r * 0.8) * side, wz = p.z + (dx / l) * (p.r * 0.8) * side;
    const s = 0.7 + ((i * 7919) % 13) / 13 * 0.9;
    out.push({ x: wx, y: p.y - p.r * 0.2 + ((i * 104729) % 7) / 7 * p.r * 0.6, z: wz, s, rx: i * 0.7, ry: i * 1.3, rz: i * 0.4 });
  }
  return out;
}

/** The crystals as emitters: a sphere of the ellipsoid's volume-equivalent radius. */
export function crystalEmitters(path: ReadonlyArray<{ x: number; y: number; z: number; r: number }>): CaveLightEmitter[] {
  const rad = crystalRadiance();
  return crystalPlacements(path).map((c) => ({ x: c.x, y: c.y, z: c.z, r: CRYSTAL_RADIUS * c.s * Math.cbrt(1.8), rad }));
}

/** A height cache on a regular grid with bilinear lookups (clamped). */
class HeightGrid {
  private readonly h: Float32Array;
  constructor(readonly x0: number, readonly z0: number, readonly step: number, readonly nx: number, readonly nz: number, fn: (x: number, z: number) => number) {
    this.h = new Float32Array(nx * nz);
    for (let j = 0; j < nz; j++) for (let i = 0; i < nx; i++) this.h[j * nx + i] = fn(x0 + i * step, z0 + j * step);
  }
  at(x: number, z: number): number {
    const gx = Math.min(this.nx - 1.0001, Math.max(0, (x - this.x0) / this.step));
    const gz = Math.min(this.nz - 1.0001, Math.max(0, (z - this.z0) / this.step));
    const i = Math.floor(gx), j = Math.floor(gz), fx = gx - i, fz = gz - j;
    const a = this.h[j * this.nx + i], b = this.h[j * this.nx + i + 1], c = this.h[(j + 1) * this.nx + i], d = this.h[(j + 1) * this.nx + i + 1];
    return (a * (1 - fx) + b * fx) * (1 - fz) + (c * (1 - fx) + d * fx) * fz;
  }
}

export interface CaveFieldOptions { spacing?: number; rays?: number; bounce?: boolean; maxProbes?: number }

/** Bake the light field of one built cave (mouth-relative, like its mesh). */
export function bakeCaveField(seed: string, site: CaveSite, plan: CavePlan, mesh: CaveMesh, opts: CaveFieldOptions = {}): CaveLightField {
  const spacing = opts.spacing ?? CAVE_PROBE_SPACING;
  // the mesh's box
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  const P = mesh.positions;
  for (let i = 0; i < P.length; i += 3) {
    const x = P[i], y = P[i + 1], z = P[i + 2];
    if (x < x0) x0 = x; if (y < y0) y0 = y; if (z < z0) z0 = z;
    if (x > x1) x1 = x; if (y > y1) y1 = y; if (z > z1) z1 = z;
  }
  if (!isFinite(x0)) { x0 = y0 = z0 = -1; x1 = y1 = z1 = 1; }
  const surfAbs = (x: number, z: number): number => terrainHeight(seed, site.x + x, site.z + z) - site.h;
  // near ground: 1 m over the mesh box + the patch margin; far hills: 8 m out to ±280 m
  const M = 24;
  const near = new HeightGrid(Math.floor(x0 - M), Math.floor(z0 - M), 1, Math.ceil(x1 - x0 + 2 * M) + 2, Math.ceil(z1 - z0 + 2 * M) + 2, surfAbs);
  const far = new HeightGrid(-280, -280, 8, 71, 71, surfAbs);
  const surf = (x: number, z: number): number => near.at(x, z);
  const air = (x: number, y: number, z: number): number => Math.min(surf(x, z) + portalDome(site, x, z) - y, caveDensity(plan, x, y, z));
  const cavity = (x: number, y: number, z: number): number => caveDensity(plan, x, y, z);
  const ground = groundPatch(x0 - M, z0 - M, x1 + M, z1 + M, 2, surf,
    (ax, ay, az, bx, by, bz, cx, cy, cz) => caveCutsTriangle(plan, ax, ay, az, bx, by, bz, cx, cy, cz));
  const occ = mergeOccluders([{ positions: mesh.positions, indices: mesh.indices, kind: 0 }, { ...ground, kind: 1 }]);
  return bakeCaveLight({
    positions: occ.positions, indices: occ.indices, triKind: occ.triKind,
    box: { x0: x0 - spacing, y0: y0 - spacing, z0: z0 - spacing, x1: x1 + spacing, y1: y1 + spacing, z1: z1 + spacing },
    spacing, maxProbes: opts.maxProbes ?? 60000,
    air, cavity, farGround: (x, z) => far.at(x, z),
    emitters: crystalEmitters(plan.path),
    rockAlbedo: CAVE_ROCK_ALBEDO, groundAlbedo: CAVE_GROUND_ALBEDO,
    rays: opts.rays ?? CAVE_PROBE_RAYS, seed: site.caveSeed | 0, bounce: opts.bounce !== false,
  });
}
